import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, User, Image, FileText, CheckCircle2, ChevronRight, ChevronLeft, SkipForward, Loader } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { personApi, PERSON_STATUSES, PROFILE_TYPES, type PersonStatus, type ProfileType } from '@/services/api/persons'
import { photoApi } from '@/services/api/photos'
import { biodataApi } from '@/services/api/biodata'
import { mrfApi } from '@/services/api/mrf'
import { tagApi } from '@/services/api/tags'
import { TagPicker } from '@/components/TagPicker'

interface PersonFormData {
  first_name: string
  last_name: string
  email: string
  phone?: string
  title?: string
  status: PersonStatus
  profile_type: ProfileType | ''
  tags: string[]
}

interface PhotoData {
  file: File | null
}

interface BiodataData {
  bio_data_title: string
  bio_data: string
}

interface MRFData {
  event_title: string
  event_date: string
  mrForm: File | null
}

type Step = 'basic' | 'photo' | 'biodata' | 'mrf' | 'confirm'

const STEPS: { id: Step; label: string; icon: React.ReactNode }[] = [
  { id: 'basic', label: 'Basic Info', icon: <User className="h-4 w-4" /> },
  { id: 'photo', label: 'Photo', icon: <Image className="h-4 w-4" /> },
  { id: 'biodata', label: 'Biodata', icon: <FileText className="h-4 w-4" /> },
  { id: 'mrf', label: 'Media Release', icon: <FileText className="h-4 w-4" /> },
  { id: 'confirm', label: 'Confirm', icon: <CheckCircle2 className="h-4 w-4" /> },
]

export default function PersonFormPageWizard() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const isEdit = !!id

  const [currentStep, setCurrentStep] = useState<Step>('basic')
  const [personFormData, setPersonFormData] = useState<PersonFormData>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    title: '',
    status: 'Private',
    profile_type: '',
    tags: []
  })

  const { data: tagOptions = [] } = useQuery({
    queryKey: ['tags'],
    queryFn: tagApi.list,
  })

  const [photoData, setPhotoData] = useState<PhotoData>({ file: null })
  const [biodataData, setBiodataData] = useState<BiodataData>({
    bio_data_title: '',
    bio_data: ''
  })
  const [mrfData, setMrfData] = useState<MRFData>({
    event_title: '',
    event_date: '',
    mrForm: null
  })

  const [skipPhoto, setSkipPhoto] = useState(false)
  const [skipBiodata, setSkipBiodata] = useState(false)
  const [skipMrf, setSkipMrf] = useState(false)
  const [createdPersonId, setCreatedPersonId] = useState<number | null>(null)
  const [isSavingRelatedData, setIsSavingRelatedData] = useState(false)

  // Get person data for editing
  const { data: person, isLoading: isLoadingPerson } = useQuery({
    queryKey: ['person', id],
    queryFn: () => personApi.getPerson(Number(id)),
    enabled: isEdit && !!id
  })

  useEffect(() => {
    if (person && isEdit) {
      setPersonFormData({
        first_name: person.first_name || '',
        last_name: person.last_name || '',
        email: person.email || '',
        phone: person.phone || '',
        title: person.title || '',
        status: (person.status as PersonStatus) || 'Private',
        profile_type: (person.profile_type as ProfileType) || '',
        tags: person.tags || []
      })
    }
  }, [person, isEdit])

  // Create person mutation
  const createMutation = useMutation({
    mutationFn: (data: PersonFormData) => personApi.createPerson(data),
    onSuccess: (newPerson) => {
      setCreatedPersonId(newPerson.id)
      queryClient.invalidateQueries({ queryKey: ['persons'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
      return newPerson
    },
    onError: (error: any) => {
      let errorMessage = 'Failed to create person'
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail
      } else if (error.message) {
        errorMessage = error.message
      }
      toast.error(errorMessage)
      throw error
    }
  })

  // Update person mutation (used in edit mode)
  const updateMutation = useMutation({
    mutationFn: (data: PersonFormData) => personApi.updatePerson(Number(id), data),
    onSuccess: (updatedPerson) => {
      setCreatedPersonId(updatedPerson.id)
      queryClient.invalidateQueries({ queryKey: ['person', Number(id)] })
      queryClient.invalidateQueries({ queryKey: ['persons'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
      return updatedPerson
    },
    onError: (error: any) => {
      let errorMessage = 'Failed to update person'
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail
      } else if (error.message) {
        errorMessage = error.message
      }
      toast.error(errorMessage)
      throw error
    }
  })

  const handlePersonSubmit = async () => {
    if (!personFormData.first_name.trim() || !personFormData.last_name.trim()) {
      toast.error('First name and last name are required')
      return
    }

    try {
      if (isEdit) {
        await updateMutation.mutateAsync(personFormData)
        toast.success('Person updated!')
      } else {
        await createMutation.mutateAsync(personFormData)
        toast.success('Person created! Now add photos and biodata.')
      }
      setCurrentStep('photo')
    } catch (error) {
      // Error is handled in mutation
    }
  }

  const handlePersonInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target
    setPersonFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file')
        return
      }
      setPhotoData({ file })
    }
  }

  const handleBiodataChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setBiodataData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleMrfChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    if (name === 'mrForm') {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (file) {
        setMrfData(prev => ({
          ...prev,
          mrForm: file
        }))
      }
    } else {
      setMrfData(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  const handleCompleteForm = async () => {
    if (!createdPersonId) {
      toast.error('Person ID not found')
      return
    }

    setIsSavingRelatedData(true)
    try {
      // Save photo if provided and not skipped
      if (!skipPhoto && photoData.file) {
        try {
          await photoApi.upload(createdPersonId, photoData.file)
          toast.success('Photo added successfully')
        } catch (error: any) {
          const errorMsg = error.response?.data?.detail || 'Failed to upload photo'
          toast.error(errorMsg)
        }
      }

      // Save biodata if provided and not skipped
      if (!skipBiodata && biodataData.bio_data) {
        try {
          await biodataApi.create(createdPersonId, {
            bio_data_title: biodataData.bio_data_title || 'Biodata',
            bio_data: biodataData.bio_data
          })
          toast.success('Biodata added successfully')
        } catch (error: any) {
          const errorMsg = error.response?.data?.detail || 'Failed to save biodata'
          toast.error(errorMsg)
        }
      }

      // Save media release form if any field was provided and not skipped.
      // Backend accepts partial input — either the title, the file, or both.
      // Previously this gated on `event_title && mrForm`, so providing only
      // one silently skipped the MRF save with no error to the user.
      if (!skipMrf && (mrfData.event_title || mrfData.mrForm || mrfData.event_date)) {
        try {
          await mrfApi.create(createdPersonId, {
            event_title: mrfData.event_title || undefined,
            event_date: mrfData.event_date || undefined,
            file: mrfData.mrForm || undefined
          })
          toast.success('Media release form added successfully')
        } catch (error: any) {
          const errorMsg = error.response?.data?.detail || 'Failed to upload media release form'
          toast.error(errorMsg)
        }
      }

      // Refresh person data
      queryClient.invalidateQueries({ queryKey: ['person', createdPersonId] })
      queryClient.invalidateQueries({ queryKey: ['persons'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })

      // Navigate to person detail page
      toast.success('Person created successfully with all data!')
      setTimeout(() => {
        navigate(`/persons/${createdPersonId}`)
      }, 500)
    } catch (error) {
      console.error('Error saving related data:', error)
      toast.error('Error saving some data. Person was created but please check related items.')
    } finally {
      setIsSavingRelatedData(false)
    }
  }

  const getStepIndex = () => STEPS.findIndex(s => s.id === currentStep)
  const stepIndex = getStepIndex()

  if (isEdit && isLoadingPerson) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div style={{ background: 'var(--bk-bg)', minHeight: '100%' }}>
      <section className="bk-page-hero">
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 8 }}>
          <button
            onClick={() => navigate('/persons')}
            className="bk-btn bk-btn-ghost bk-btn-sm"
          >
            <ArrowLeft size={14} /> Back to persons
          </button>
        </div>
        <h1 className="bk-page-title">{isEdit ? 'Edit person' : 'Add new person'}</h1>
        <p className="bk-page-sub">
          {isEdit ? 'Update person information.' : 'Create a new person profile — all steps after Basic info are optional.'}
        </p>
      </section>

      <div style={{ maxWidth: 880, margin: '0 auto', padding: '24px 32px 64px' }}>

      {/* Progress Steps */}
      <div style={{ marginBottom: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          {STEPS.map((step, idx) => {
            const active = currentStep === step.id
            const done = idx < stepIndex
            return (
              <div key={step.id} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <button
                  onClick={() => setCurrentStep(step.id)}
                  disabled={idx > stepIndex && !isEdit}
                  type="button"
                  style={{
                    display: 'grid', placeItems: 'center',
                    width: 36, height: 36, borderRadius: '50%',
                    fontSize: 13, fontWeight: 700,
                    border: '1px solid ' + (active ? 'var(--bk-accent)' : done ? 'var(--bk-success)' : 'var(--bk-border-strong)'),
                    background: active ? 'var(--bk-accent)' : done ? 'var(--bk-success)' : 'var(--bk-surface)',
                    color: active || done ? '#fff' : 'var(--bk-muted)',
                    cursor: idx > stepIndex && !isEdit ? 'not-allowed' : 'pointer',
                    transition: 'all .15s',
                  }}
                  aria-current={active ? 'step' : undefined}
                >
                  {done ? '✓' : idx + 1}
                </button>
                {idx < STEPS.length - 1 && (
                  <div style={{
                    flex: 1, height: 2, margin: '0 8px',
                    background: done ? 'var(--bk-success)' : 'var(--bk-border)',
                  }} />
                )}
              </div>
            )
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          {STEPS.map(step => (
            <span key={step.id} style={{ flex: 1, textAlign: 'center', fontSize: 11.5, color: 'var(--bk-muted)', fontWeight: 500 }}>
              {step.label}
            </span>
          ))}
        </div>
      </div>

      {/* Form Content */}
      <div className="bk-card">
        <div className="bk-card-pad">
          {/* Step 1: Basic Information */}
          {currentStep === 'basic' && (
            <div className="space-y-6">
              <div>
                <h3 className="bk-display"
                  style={{ fontSize: 18, fontWeight: 600, marginBottom: 14, display: 'flex', alignItems: 'center', color: 'var(--bk-ink)' }}>
                  <User className="h-5 w-5 mr-2" />
                  Basic Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="first_name" className="bk-label">
                      First Name *
                    </label>
                    <input
                      type="text"
                      id="first_name"
                      name="first_name"
                      value={personFormData.first_name}
                      onChange={handlePersonInputChange}
                      className="bk-input"
                      placeholder="Enter first name"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="last_name" className="bk-label">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      id="last_name"
                      name="last_name"
                      value={personFormData.last_name}
                      onChange={handlePersonInputChange}
                      className="bk-input"
                      placeholder="Enter last name"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="title" className="bk-label">
                      Title
                    </label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={personFormData.title}
                      onChange={handlePersonInputChange}
                      className="bk-input"
                      placeholder="e.g., BK, Dr., Mr., Ms."
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="bk-label">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={personFormData.email}
                      onChange={handlePersonInputChange}
                      className="bk-input"
                      placeholder="Enter email address"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="phone" className="bk-label">
                      Phone
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={personFormData.phone}
                      onChange={handlePersonInputChange}
                      className="bk-input"
                      placeholder="Enter phone number"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="status" className="bk-label">
                      Status
                    </label>
                    <select
                      id="status"
                      name="status"
                      value={personFormData.status}
                      onChange={handlePersonInputChange}
                      className="bk-select"
                    >
                      {PERSON_STATUSES.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    <p className="text-xs text-muted-foreground mt-1">
                      Private profiles are hidden from regular users. Public profiles are visible to all users. Inactive profiles are hidden.
                    </p>
                  </div>

                  <div>
                    <label htmlFor="profile_type" className="bk-label">
                      Profile Type
                    </label>
                    <select
                      id="profile_type"
                      name="profile_type"
                      value={personFormData.profile_type}
                      onChange={handlePersonInputChange}
                      className="bk-select"
                    >
                      <option value="">— None —</option>
                      {PROFILE_TYPES.map(pt => (
                        <option key={pt} value={pt}>{pt}</option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="bk-label">Tags</label>
                    <TagPicker
                      value={personFormData.tags}
                      onChange={(tags) => setPersonFormData(prev => ({ ...prev, tags }))}
                      options={tagOptions}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Photo */}
          {currentStep === 'photo' && (
            <div className="space-y-6">
              <div>
                <h3 className="bk-display"
                  style={{ fontSize: 18, fontWeight: 600, marginBottom: 14, display: 'flex', alignItems: 'center', color: 'var(--bk-ink)' }}>
                  <Image className="h-5 w-5 mr-2" />
                  Add Photo
                </h3>

                {photoData.file ? (
                  <div className="mb-4">
                    <p className="text-sm text-muted-foreground mb-2">Selected file:</p>
                    <div className="flex items-center justify-between bg-gray-100 p-3 rounded">
                      <span className="text-sm font-medium">{photoData.file.name}</span>
                      <button
                        type="button"
                        onClick={() => setPhotoData({ file: null })}
                        className="text-red-500 text-sm hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mb-4">
                    <label htmlFor="photo" className="bk-label">
                      Upload Photo
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-primary hover:bg-gray-50">
                      <input
                        type="file"
                        id="photo"
                        name="photo"
                        accept="image/*"
                        onChange={handlePhotoChange}
                        className="hidden"
                      />
                      <label htmlFor="photo" className="cursor-pointer block">
                        <Image className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm font-medium text-foreground">Click to upload or drag and drop</p>
                        <p className="text-xs text-muted-foreground">PNG, JPG, GIF up to 10MB</p>
                      </label>
                    </div>
                  </div>
                )}

                <p className="text-xs text-muted-foreground">
                  You can add more photos later from the person detail page
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Biodata */}
          {currentStep === 'biodata' && (
            <div className="space-y-6">
              <div>
                <h3 className="bk-display"
                  style={{ fontSize: 18, fontWeight: 600, marginBottom: 14, display: 'flex', alignItems: 'center', color: 'var(--bk-ink)' }}>
                  <FileText className="h-5 w-5 mr-2" />
                  Add Biodata
                </h3>

                <div className="space-y-4">
                  <div>
                    <label htmlFor="bio_data_title" className="bk-label">
                      Biodata Title
                    </label>
                    <input
                      type="text"
                      id="bio_data_title"
                      name="bio_data_title"
                      value={biodataData.bio_data_title}
                      onChange={handleBiodataChange}
                      className="bk-input"
                      placeholder="e.g., Life Summary, Biography"
                    />
                  </div>

                  <div>
                    <label htmlFor="bio_data" className="bk-label">
                      Biodata Content
                    </label>
                    <textarea
                      id="bio_data"
                      name="bio_data"
                      value={biodataData.bio_data}
                      onChange={handleBiodataChange}
                      className="bk-textarea"
                      style={{ minHeight: 160 }}
                      placeholder="Enter biographical information..."
                    />
                  </div>
                </div>

                <p className="text-xs text-muted-foreground mt-4">
                  You can add more biodata entries later
                </p>
              </div>
            </div>
          )}

          {/* Step 4: Media Release Form */}
          {currentStep === 'mrf' && (
            <div className="space-y-6">
              <div>
                <h3 className="bk-display"
                  style={{ fontSize: 18, fontWeight: 600, marginBottom: 14, display: 'flex', alignItems: 'center', color: 'var(--bk-ink)' }}>
                  <FileText className="h-5 w-5 mr-2" />
                  Media Release Form
                </h3>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="event_title" className="bk-label">
                        Event Title {mrfData.mrForm && <span className="text-red-600">*</span>}
                      </label>
                      <input
                        type="text"
                        id="event_title"
                        name="event_title"
                        value={mrfData.event_title}
                        onChange={handleMrfChange}
                        className="bk-input"
                        placeholder="e.g., Annual Conference 2024"
                      />
                      {mrfData.mrForm && !mrfData.event_title.trim() && (
                        <p className="text-xs text-red-600 mt-1">
                          Event title is required when a file is uploaded.
                        </p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="event_date" className="bk-label">
                        Event Date
                      </label>
                      <input
                        type="date"
                        id="event_date"
                        name="event_date"
                        value={mrfData.event_date}
                        onChange={handleMrfChange}
                        className="bk-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="mrForm" className="bk-label">
                      Upload Media Release Form
                    </label>
                    {mrfData.mrForm ? (
                      <div className="flex items-center justify-between bg-gray-100 p-3 rounded">
                        <span className="text-sm font-medium">{mrfData.mrForm.name}</span>
                        <button
                          type="button"
                          onClick={() => setMrfData(prev => ({ ...prev, mrForm: null }))}
                          className="text-red-500 text-sm hover:text-red-700"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-primary hover:bg-gray-50">
                        <input
                          type="file"
                          id="mrForm"
                          name="mrForm"
                          accept=".pdf,.doc,.docx"
                          onChange={handleMrfChange}
                          className="hidden"
                        />
                        <label htmlFor="mrForm" className="cursor-pointer block">
                          <FileText className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                          <p className="text-sm font-medium text-foreground">Click to upload or drag and drop</p>
                          <p className="text-xs text-muted-foreground">PDF, DOC, DOCX up to 10MB</p>
                        </label>
                      </div>
                    )}
                  </div>
                </div>

                <p className="text-xs text-muted-foreground mt-4">
                  You can add more media release forms later
                </p>
              </div>
            </div>
          )}

          {/* Step 5: Confirmation */}
          {currentStep === 'confirm' && (
            <div className="space-y-6">
              <div>
                <h3 className="bk-display"
                  style={{ fontSize: 18, fontWeight: 600, marginBottom: 14, display: 'flex', alignItems: 'center', color: 'var(--bk-ink)' }}>
                  <CheckCircle2 className="h-5 w-5 mr-2" />
                  Review & Confirm
                </h3>

                <div className="space-y-4">
                  <div className="bg-blue-50 p-4 rounded border border-blue-200">
                    <p className="font-semibold text-foreground mb-2">Person Information</p>
                    <p className="text-sm text-muted-foreground">
                      <span className="font-medium">{personFormData.first_name} {personFormData.last_name}</span>
                      {personFormData.email && ` - ${personFormData.email}`}
                    </p>
                  </div>

                  {!skipPhoto && photoData.file && (
                    <div className="bg-green-50 p-4 rounded border border-green-200">
                      <p className="font-semibold text-foreground mb-2">✓ Photo Added</p>
                      <p className="text-sm text-muted-foreground">{photoData.file.name}</p>
                    </div>
                  )}

                  {!skipBiodata && biodataData.bio_data && (
                    <div className="bg-green-50 p-4 rounded border border-green-200">
                      <p className="font-semibold text-foreground mb-2">✓ Biodata Added</p>
                      <p className="text-sm text-muted-foreground">{biodataData.bio_data_title || 'Untitled'}</p>
                    </div>
                  )}

                  {!skipMrf && (mrfData.event_title || mrfData.mrForm || mrfData.event_date) && (
                    <div className="bg-green-50 p-4 rounded border border-green-200">
                      <p className="font-semibold text-foreground mb-2">✓ Media Release Form Added</p>
                      <p className="text-sm text-muted-foreground">
                        {mrfData.event_title || mrfData.mrForm?.name || mrfData.event_date}
                      </p>
                    </div>
                  )}

                  <p className="text-sm text-muted-foreground mt-6">
                    Click "Complete" to save everything. You can edit details later.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 22, borderTop: '1px solid var(--bk-border)', marginTop: 28 }}>
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={() => {
                  const currentIndex = STEPS.findIndex(s => s.id === currentStep)
                  if (currentIndex > 0) {
                    setCurrentStep(STEPS[currentIndex - 1].id)
                  }
                }}
                disabled={currentStep === 'basic'}
                className="bk-btn bk-btn-secondary"
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back
              </button>

              {(currentStep === 'photo' || currentStep === 'biodata' || currentStep === 'mrf') && (
                <button
                  type="button"
                  onClick={() => {
                    if (currentStep === 'photo') setSkipPhoto(true)
                    if (currentStep === 'biodata') setSkipBiodata(true)
                    if (currentStep === 'mrf') setSkipMrf(true)

                    const currentIndex = STEPS.findIndex(s => s.id === currentStep)
                    if (currentIndex < STEPS.length - 1) {
                      setCurrentStep(STEPS[currentIndex + 1].id)
                    }
                  }}
                  className="bk-btn bk-btn-secondary"
                >
                  <SkipForward className="h-4 w-4 mr-2" />
                  Skip
                </button>
              )}
            </div>

            <div className="flex space-x-2">
              {currentStep === 'basic' && (
                <button
                  type="button"
                  onClick={handlePersonSubmit}
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="bk-btn bk-btn-primary"
                >
                  {(createMutation.isPending || updateMutation.isPending) ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {isEdit ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      <ChevronRight className="h-4 w-4 mr-2" />
                      Next
                    </>
                  )}
                </button>
              )}

              {(currentStep === 'photo' || currentStep === 'biodata' || currentStep === 'mrf') && (
                <button
                  type="button"
                  onClick={() => {
                    if (currentStep === 'mrf' && mrfData.mrForm && !mrfData.event_title.trim()) {
                      toast.error('Event title is required when a file is uploaded')
                      return
                    }
                    const currentIndex = STEPS.findIndex(s => s.id === currentStep)
                    if (currentIndex < STEPS.length - 1) {
                      setCurrentStep(STEPS[currentIndex + 1].id)
                    }
                  }}
                  disabled={currentStep === 'mrf' && !!mrfData.mrForm && !mrfData.event_title.trim()}
                  className="bk-btn bk-btn-primary"
                >
                  <ChevronRight className="h-4 w-4 mr-2" />
                  Next
                </button>
              )}

              {currentStep === 'confirm' && (
                <button
                  type="button"
                  onClick={handleCompleteForm}
                  disabled={isSavingRelatedData || (!skipMrf && !!mrfData.mrForm && !mrfData.event_title.trim())}
                  className="bk-btn bk-btn-primary"
                >
                  {isSavingRelatedData ? (
                    <>
                      <Loader className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Complete
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  )
}
